import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import InformationBox from '@repo/ui/information-box'
import Typography from '@repo/ui/typography'
import { t } from 'i18next'
import { z } from 'zod'

import ArrowRightOneIcon from '../../../../../../packages/ui/icons/arrow-right-01'
import useMultipleSchema from '../../../lib/hooks/use-multiple-schema'
import { AffectedStaffDailyReportInput } from '../../../lib/types/models/affectedStaff/affected-staff-daily-report'
import { NullableTransformer } from '../../../lib/types/utils'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { mapDailyReportIssues } from '../../../views/staff-personal-space/utils'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'

const saveForLaterSchema: z.ZodType<NullableTransformer<AffectedStaffDailyReportInput>> = z.object(
  {
    date: createDateStringSchema({ inputName: 'Date' }).nullish(),
    previousIssuesProgress: createStringSchema(
      'Progrès réalisés dans la résolution des problèmes précédemment signalés'
    ).nullish(),
    incidentDetails: createStringSchema('Détails de tout incident ou problème survenu').nullish(),

    disciplineIssue: z.boolean(),
    healthIssue: z.boolean(),
    drugsIssue: z.boolean(),
    accidentIssue: z.boolean(),
    damageIssue: z.boolean(),
    theftIssue: z.boolean(),
    conductIssue: z.boolean(),
    violenceIssue: z.boolean(),
    moralHarassmentIssue: z.boolean(),
    sexualHarassmentIssue: z.boolean(),
    teamIssue: z.boolean(),
    otherIssue: z.boolean()
  }
)

const incidentDetailsFieldName = 'Détails de tout incident ou problème survenu'

// question 3 is only required when at least one reason of question 2 is answered "Oui";
// checked in its own schema so the error shows together with the date / question 1 errors
const submissionIssuesSchema = z
  .object({
    incidentDetails: createStringSchema(incidentDetailsFieldName, { nullish: true }),

    disciplineIssue: z.boolean(),
    healthIssue: z.boolean(),
    drugsIssue: z.boolean(),
    accidentIssue: z.boolean(),
    damageIssue: z.boolean(),
    theftIssue: z.boolean(),
    conductIssue: z.boolean(),
    violenceIssue: z.boolean(),
    moralHarassmentIssue: z.boolean(),
    sexualHarassmentIssue: z.boolean(),
    teamIssue: z.boolean(),
    otherIssue: z.boolean()
  })
  .superRefine((data, ctx) => {
    const hasIssue = Object.keys(mapDailyReportIssues).some(
      (key) => data[key as keyof typeof mapDailyReportIssues]
    )
    if (hasIssue && !data.incidentDetails)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('errors.string.required', { fieldName: incidentDetailsFieldName }),
        path: ['incidentDetails']
      })
  })

const submissionSchema: z.ZodType<NullableTransformer<AffectedStaffDailyReportInput>> = z
  .object({
    date: createDateStringSchema({ inputName: 'Date' }),
    previousIssuesProgress: createStringSchema(
      'Progrès réalisés dans la résolution des problèmes précédemment signalés'
    )
  })
  .and(submissionIssuesSchema)

type Props = {
  defaultValues?: AffectedStaffDailyReportInput
  handleSubmit: (
    data: AffectedStaffDailyReportInput,
    type: 'submit' | 'save-for-later'
  ) => Promise<void>
}
export default function AffectedStaffDailyReportForm({ defaultValues, handleSubmit }: Props) {
  const { form, loading, submitForm } = useMultipleSchema<
    ['save-for-later', 'submit'],
    AffectedStaffDailyReportInput
  >(
    [
      { name: 'save-for-later', schema: saveForLaterSchema },
      { name: 'submit', schema: submissionSchema }
    ],
    handleSubmit,
    defaultValues
  )

  return (
    <Form form={form}>
      <form className="flex flex-col gap-4">
        <InformationBox>
          <div className="flex flex-col gap-1">
            <Typography variant="caption">
              Nous envoyer par mail, au plus tard 3 jours après l’arrivée des jeunes
            </Typography>
            <div className="flex items-center gap-1">
              <div className="flex items-center justify-center gap-1 bg-red-light w-5 h-5 rounded-full">
                <ArrowRightOneIcon className="w-4 h-4" color={tailwindColors.red.DEFAULT} />
              </div>
              <Typography variant="secondary-body">
                Le formulaire : « Handover / Take back conditions »
              </Typography>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex items-center justify-center gap-1 bg-red-light w-5 h-5 rounded-full">
                <ArrowRightOneIcon className="w-4 h-4" color={tailwindColors.red.DEFAULT} />
              </div>
              <Typography variant="secondary-body">
                La date à laquelle a eu lieu l’exercice incendie{' '}
              </Typography>
            </div>
          </div>
        </InformationBox>
        <Box className="flex flex-col gap-3 bg-white-hover rounded-lg border border-grey-light">
          <Typography variant="body-medium" className="text-primary">
            Journée du
          </Typography>
          <FormField
            control={form.control}
            name={'date'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    labelClassName="bg-white-hover"
                    label="Journée du"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Box>
        <div className="flex gap-4 mobile:flex-col flex-row">
          <Box className="flex flex-col gap-3 rounded-lg border border-grey-light flex-1">
            <Badge
              size="flexible"
              className="p-2"
              variant="primary"
              text={'1 - Évolution des problèmes évoqués sur les rapports précédents '}
            />
            <FormField
              control={form.control}
              name={'previousIssuesProgress'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      className="h-full"
                      wrapperClassName="h-full"
                      containerClassName="h-full"
                      inputClassName="h-full min-h-48"
                      type="textarea"
                      label="Suivi"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </Box>

          <Box className="flex flex-col gap-3 rounded-lg border border-grey-light flex-1">
            <Badge
              size="flexible"
              variant="primary"
              className="p-2"
              text={
                '2 - Avez-vous rencontré des problèmes pour les motifs suivants (réponse obligatoire pour chacun des motifs) '
              }
            />
            <Typography variant="label-medium" className="text-grey">
              Si vous estimez que la nature des problèmes est trop importante pour être indiquée
              sur ce document nous vous demandons d’appeler nos bureaux à Paris (01 42 67 20 00).
            </Typography>

            {Object.keys(mapDailyReportIssues).map((key) => (
              <div className="flex flex-row justify-between items-center" key={key}>
                <Typography variant="tag" className="text-grey">
                  {mapDailyReportIssues[key as keyof typeof mapDailyReportIssues]}
                </Typography>
                <div className="flex gap-4 items-center">
                  <FormField
                    control={form.control}
                    name={key as keyof AffectedStaffDailyReportInput}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={'Oui'}
                            checked={field.value === true}
                            onCheck={() =>
                              form.setValue(key as keyof AffectedStaffDailyReportInput, true, {
                                shouldValidate: true
                              })
                            }
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={key as keyof AffectedStaffDailyReportInput}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={'Non'}
                            checked={field.value === false}
                            onCheck={() =>
                              form.setValue(key as keyof AffectedStaffDailyReportInput, false, {
                                shouldValidate: true
                              })
                            }
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            ))}
          </Box>
        </div>
        <Box className="flex flex-col gap-3 rounded-lg border border-grey-light flex-1">
          <Badge
            size="flexible"
            variant="primary"
            className="p-2"
            text={
              <div>
                3 - IMPERATIF : Si vous avez coché "oui" à la question 2 :<br />
                Précisez ci-dessous le nom du ou des jeune(s) / membre de l'encadrement
                concerné(s), la nature du problème et la date/heure + nom de la personne qui a
                appelé les parents.
              </div>
            }
          />
          <div className="flex flex-col gap-1">
            <Typography variant="caption">De plus en cas de :</Typography>
            <div className="flex items-center gap-1">
              <ArrowRightOneIcon className="w-4 h-4" color={tailwindColors.red.DEFAULT} />
              <Typography variant="secondary-body" className="text-grey">
                Problème de santé : nous indiquer le nom du traitement prescrit par le médecin.
              </Typography>
            </div>

            <div className="flex items-center gap-1">
              <ArrowRightOneIcon className="w-4 h-4" color={tailwindColors.red.DEFAULT} />
              <Typography variant="secondary-body" className="text-grey">
                Problème de vol, de dégradation, de discipline ou de drogue : remplir le document «
                rapport d’événement / constatation de vol » et nous en envoyer une copie + photos
                si dégradation.
              </Typography>
            </div>
          </div>

          <FormField
            control={form.control}
            name={'incidentDetails'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    inputClassName="h-48"
                    type="textarea"
                    label="Write here"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Box>

        <div className="flex justify-end mt-4 gap-2.5 mobile:flex-col mobile:gap-2">
          <Button
            label="Enregistrer et continuer plus tard"
            variant="ghost"
            type="button"
            loading={loading['save-for-later']}
            onClick={() => {
              submitForm('save-for-later')
            }}
          />
          <Button
            label="Valider et envoyer à LEC"
            variant="primary"
            type="button"
            loading={loading.submit}
            onClick={() => {
              submitForm('submit')
            }}
          />
        </div>
      </form>
    </Form>
  )
}
